import { useEffect, useRef } from 'react'
import { useClientPreferences } from '../../context/useClientPreferences'
import {
  addDebugScreenshot,
  appendDebugSessionEvent,
  describeDebugElement,
  startDebugSession,
  stopDebugSession,
} from '../../utils/debugSessionRecorder'
import { captureDebugScreenshot } from '../../utils/debugScreenshot'

const AUTO_SCREENSHOT_THROTTLE_MS = 1500
const PRINTABLE_KEY = /^.$/u

const stripQuery = (url: string) => {
  try {
    const parsed = new URL(url, window.location.origin)
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return url.split('?')[0]
  }
}

/**
 * True when the event's target sits inside a subtree marked with
 * `data-debug-ignore="true"` — the debug pill, its popover, its dialogs,
 * and any toast we surface during the flow. These clicks/keystrokes are
 * meta-actions (opening the panel, adding a note, toggling the pill) and
 * must not pollute the reproduction timeline.
 */
const isDebugMetaTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return false
  return Boolean(target.closest('[data-debug-ignore="true"]'))
}

export const DebugSessionRecorder = () => {
  const { preferences } = useClientPreferences()
  const previousPathRef = useRef<string>('')
  const lastAutoShotRef = useRef<number>(0)

  useEffect(() => {
    if (!preferences.enableDebugMode) {
      stopDebugSession()
      return
    }

    startDebugSession()
    previousPathRef.current = `${window.location.pathname}${window.location.search}${window.location.hash}`

    const runAutoScreenshot = async (source: 'auto' | 'error') => {
      const now = Date.now()
      if (source === 'auto' && now - lastAutoShotRef.current < AUTO_SCREENSHOT_THROTTLE_MS) return
      lastAutoShotRef.current = now
      const capture = await captureDebugScreenshot().catch(() => null)
      if (!capture) return
      await addDebugScreenshot({
        blob: capture.blob,
        width: capture.width,
        height: capture.height,
        capturedAt: capture.capturedAt,
        source,
      }).catch(() => undefined)
    }

    const recordRoute = (source: string) => {
      const nextPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
      if (nextPath === previousPathRef.current) return
      const previousPath = previousPathRef.current
      previousPathRef.current = nextPath
      appendDebugSessionEvent('route', { source, from: previousPath, to: nextPath }, { includeState: true })
    }

    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState
    window.history.pushState = function patchedPushState(...args) {
      const result = originalPushState.apply(this, args)
      recordRoute('pushState')
      return result
    }
    window.history.replaceState = function patchedReplaceState(...args) {
      const result = originalReplaceState.apply(this, args)
      recordRoute('replaceState')
      return result
    }

    const handlePopState = () => recordRoute('popstate')
    const handleHashChange = () => recordRoute('hashchange')
    const handleClick = (event: MouseEvent) => {
      if (isDebugMetaTarget(event.target)) return
      appendDebugSessionEvent('click', {
        button: event.button,
        target: describeDebugElement(event.target),
        pointer: { x: Math.round(event.clientX), y: Math.round(event.clientY) },
      }, { includeState: true })
      void runAutoScreenshot('auto')
    }
    const handleInput = (event: Event) => {
      if (isDebugMetaTarget(event.target)) return
      appendDebugSessionEvent('input', { target: describeDebugElement(event.target) })
    }
    const handleSubmit = (event: SubmitEvent) => {
      if (isDebugMetaTarget(event.target)) return
      appendDebugSessionEvent('submit', { target: describeDebugElement(event.target) }, { includeState: true })
    }
    const handleFocus = (event: FocusEvent) => {
      if (isDebugMetaTarget(event.target)) return
      appendDebugSessionEvent('focus', { target: describeDebugElement(event.target) })
    }
    const handleVisibility = () => {
      appendDebugSessionEvent('visibility', { state: document.visibilityState })
    }
    const handleError = (event: ErrorEvent) => {
      appendDebugSessionEvent('error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }, { includeState: true })
      void runAutoScreenshot('error')
    }
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      appendDebugSessionEvent('error', {
        message: event.reason instanceof Error ? event.reason.message : String(event.reason),
        kind: 'unhandledrejection',
      }, { includeState: true })
      void runAutoScreenshot('error')
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isDebugMetaTarget(event.target)) return
      const isPrintable = PRINTABLE_KEY.test(event.key)
      const hasModifier = event.ctrlKey || event.metaKey || event.altKey
      if (isPrintable && !hasModifier) return
      appendDebugSessionEvent('keydown', {
        key: event.key,
        code: event.code,
        modifiers: {
          alt: event.altKey || undefined,
          ctrl: event.ctrlKey || undefined,
          meta: event.metaKey || undefined,
          shift: event.shiftKey || undefined,
        },
        target: describeDebugElement(event.target),
      })
    }
    const handleResize = () => {
      appendDebugSessionEvent('viewport', {
        size: `${window.innerWidth}x${window.innerHeight}`,
        dpr: window.devicePixelRatio || 1,
      })
    }

    // Patch console.error / console.warn
    const originalConsoleError = console.error
    const originalConsoleWarn = console.warn
    console.error = function patchedConsoleError(...args: unknown[]) {
      try {
        appendDebugSessionEvent('console', {
          level: 'error',
          text: args.map((a) => (a instanceof Error ? a.stack || a.message : String(a))).join(' ').slice(0, 800),
        })
      } catch { /* ignore */ }
      return originalConsoleError.apply(this, args)
    }
    console.warn = function patchedConsoleWarn(...args: unknown[]) {
      try {
        appendDebugSessionEvent('console', {
          level: 'warn',
          text: args.map((a) => String(a)).join(' ').slice(0, 800),
        })
      } catch { /* ignore */ }
      return originalConsoleWarn.apply(this, args)
    }

    // Patch fetch
    const originalFetch = window.fetch
    window.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
      const started = performance.now()
      const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase()
      const rawUrl = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url
      const url = stripQuery(rawUrl)
      try {
        const response = await originalFetch.call(this, input, init)
        appendDebugSessionEvent('network', {
          transport: 'fetch',
          method,
          url,
          status: response.status,
          ok: response.ok,
          durationMs: Math.round(performance.now() - started),
        })
        return response
      } catch (error) {
        appendDebugSessionEvent('network', {
          transport: 'fetch',
          method,
          url,
          ok: false,
          error: error instanceof Error ? error.message : String(error),
          durationMs: Math.round(performance.now() - started),
        })
        throw error
      }
    }

    // Patch XHR
    const originalXhrOpen = XMLHttpRequest.prototype.open
    const originalXhrSend = XMLHttpRequest.prototype.send
    XMLHttpRequest.prototype.open = function patchedOpen(this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
      ;(this as unknown as { __debugMethod?: string; __debugUrl?: string }).__debugMethod = method.toUpperCase()
      ;(this as unknown as { __debugUrl?: string }).__debugUrl = stripQuery(typeof url === 'string' ? url : url.toString())
      return originalXhrOpen.apply(this, [method, url, ...(rest as [boolean, string?, string?])] as Parameters<typeof originalXhrOpen>)
    } as typeof XMLHttpRequest.prototype.open
    XMLHttpRequest.prototype.send = function patchedSend(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
      const started = performance.now()
      const meta = this as unknown as { __debugMethod?: string; __debugUrl?: string }
      const method = meta.__debugMethod || 'GET'
      const url = meta.__debugUrl || 'unknown'
      const onDone = () => {
        appendDebugSessionEvent('network', {
          transport: 'xhr',
          method,
          url,
          status: this.status,
          ok: this.status >= 200 && this.status < 400,
          durationMs: Math.round(performance.now() - started),
        })
      }
      this.addEventListener('loadend', onDone, { once: true })
      return originalXhrSend.call(this, body ?? null)
    } as typeof XMLHttpRequest.prototype.send

    window.addEventListener('popstate', handlePopState)
    window.addEventListener('hashchange', handleHashChange)
    document.addEventListener('click', handleClick, true)
    document.addEventListener('input', handleInput, true)
    document.addEventListener('change', handleInput, true)
    document.addEventListener('submit', handleSubmit, true)
    document.addEventListener('focusin', handleFocus, true)
    document.addEventListener('keydown', handleKeyDown, true)
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    window.addEventListener('resize', handleResize)

    return () => {
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
      window.fetch = originalFetch
      XMLHttpRequest.prototype.open = originalXhrOpen
      XMLHttpRequest.prototype.send = originalXhrSend
      console.error = originalConsoleError
      console.warn = originalConsoleWarn
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('hashchange', handleHashChange)
      document.removeEventListener('click', handleClick, true)
      document.removeEventListener('input', handleInput, true)
      document.removeEventListener('change', handleInput, true)
      document.removeEventListener('submit', handleSubmit, true)
      document.removeEventListener('focusin', handleFocus, true)
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      window.removeEventListener('resize', handleResize)
    }
  }, [preferences.enableDebugMode])

  return null
}
